import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Button, Group, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/feedback';
import { useT } from '../../i18n/I18nProvider';

/**
 * Unverified-email prompt, promoted from the dashboard into the shell
 * (`_UI_improvements/tenant/_app-shell.md` §5). It used to live only on
 * `pages/dashboard`, so navigating to Contacts made the warning disappear
 * even though the account was still unverified.
 *
 * Renders nothing once `user.email_verified` flips, and nothing on the auth
 * pages (the shell only wraps authenticated routes).
 */
export function EmailVerificationBanner() {
  const { user, resendVerification } = useAuth();
  const t = useT();
  const [busy, setBusy] = useState(false);

  if (!user || user.email_verified) return null;

  async function resend() {
    if (!user?.email) return;
    setBusy(true);
    try {
      await resendVerification(user.email);
      notifySuccess(t('auth.verify.resent', 'Verification email sent.'));
    } catch {
      // The previous dashboard banner had no resend at all; when the tenant
      // onboarding wizard added one it fired unawaited with no catch, so a
      // failure surfaced as an unhandled rejection and looked like success.
      notifyError(t('auth.login.errors.unavailable'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Alert color="warning" className="rr-verify-banner" title={t('shell.verifyEmail.title')}>
      <Group justify="space-between" align="center" wrap="nowrap" gap="md">
        <Text role="bodySmall">
          {t('shell.verifyEmail.body', { email: user.email ?? '' })}{' '}
          <Link to="/auth/verify-email">{t('dashboard.verifyBanner.cta', 'Verify now')}</Link>
        </Text>
        <Button size="xs" variant="default" loading={busy} onClick={() => { void resend(); }}>
          {t('shell.verifyEmail.action')}
        </Button>
      </Group>
    </Alert>
  );
}
